import { format } from 'date-fns'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalAnalystsQuery } from '../../api/analysts'
import {
  invalidateInternalReports,
  useCreateInternalReportMutation,
  useGetInternalReportQuery,
  useGetInternalReportTagsQuery,
  useLazyGetInternalReportQuery,
  useLazyGetInternalReportsQuery,
  useUpdateInternalReportMutation,
} from '../../api/reports'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { ReportForm } from '../../features/reports/components/ReportForm/ReportForm'
import {
  getUnstoredReportFields,
  needsPublishConfirmation,
  reportFieldLabelKeys,
  ReportFormValues,
  reportVisibilityOptions,
  toReportFormValues,
  toReportMutation,
} from '../../features/reports/helpers/reportHelpers'
import { ReportUploadsContext, useReportUploads } from '../../features/reports/helpers/reportUploads'
import { InternalReport, InternalReportMutation } from '../../features/reports/types/InternalReport'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

export const reportsListPath = '/internal/reports'

export const reportEditPath = (id?: string) => `/internal/reports/edit${id ? `/${id}` : ''}`

export const ReportEditPage: FC = () => {
  const { reportId } = useParams<{ reportId?: string }>()
  usePage(pageService.getInternalPageWithId('internal-report-edit'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const reportQuery = useGetInternalReportQuery(reportId ?? '', { skip: !reportId })
  const analystsQuery = useGetInternalAnalystsQuery()
  const tagsQuery = useGetInternalReportTagsQuery()
  const [createReport] = useCreateInternalReportMutation()
  const [updateReport] = useUpdateInternalReportMutation()
  const [readReport] = useLazyGetInternalReportQuery()
  const [readReports] = useLazyGetInternalReportsQuery()
  const [isSaving, setIsSaving] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<ReportFormValues>>()
  const uploads = useReportUploads()
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ReportFormValues>({ mode: 'onChange', defaultValues: toReportFormValues(undefined) })
  const { guard, leave } = useEditPageLeave(isDirty || uploads.isUploading)
  const report = reportId ? reportQuery.data : undefined
  const loadedReportId = useRef<string>()

  // Only on the first load of a report, so a refetch after saving can't replace edits that weren't stored.
  useEffect(() => {
    if (report && loadedReportId.current !== report.id) {
      loadedReportId.current = report.id
      reset(toReportFormValues(report))
    }
  }, [report, reset])

  // The write's answer isn't trusted: the report is read again. A failed create is looked up by its uploaded file.
  const readBack = useCallback(
    async (body: InternalReportMutation, id?: string): Promise<InternalReport | undefined> => {
      if (id) return (await readReport(id)).data
      const reports = (await readReports()).data
      return reports?.find((stored) => stored.fileId === body.fileId && stored.title?.en === body.title.en)
    },
    [readReport, readReports]
  )

  const save = useCallback(
    async (values: ReportFormValues) => {
      const body = toReportMutation(report, values)
      setIsSaving(true)
      let answer: InternalReport | undefined
      let failure: unknown
      try {
        answer = await (report ? updateReport(body) : createReport(body)).unwrap()
      } catch (error) {
        failure = error
      }
      const stored = await readBack(body, report?.id ?? answer?.id)
      dispatch(invalidateInternalReports())
      setIsSaving(false)

      if (!stored) {
        const reason = getValidationErrorMessage(failure)
        notifyError(reason ? t('internal-analysts:reports_save_failed_reason', { reason }) : t('internal-analysts:reports_save_failed'))
        return
      }
      if (!report) {
        // Keeps the values on the form when the created report opens for editing.
        loadedReportId.current = stored.id
      }
      const unstored = getUnstoredReportFields(body, stored)
      if (unstored.length || failure) {
        const fields = unstored.map((field) => t(reportFieldLabelKeys[field])).join(', ')
        notifyError(unstored.length ? t('internal-analysts:reports_not_stored', { fields }) : t('internal-analysts:reports_saved_despite_error'))
        if (!report) leave(reportEditPath(stored.id), { replace: true })
        return
      }
      notifySuccess(t('internal-analysts:reports_saved', { name: values.titles.en }))
      leave(reportsListPath)
    },
    [createReport, dispatch, leave, notifyError, notifySuccess, readBack, report, t, updateReport]
  )

  const handleSave = handleSubmit(
    (values) => {
      if (uploads.isUploading) return
      if (!needsPublishConfirmation(report, values)) {
        save(values)
        return
      }
      const visibility = reportVisibilityOptions.find((option) => option.value === values.visibility)
      setConfirmDialogData({
        title: t('internal-analysts:reports_publish_title'),
        confirmText: (
          <Stack spacing={1}>
            <Typography variant="body2">
              {values.publishedAt > Date.now()
                ? t('internal-analysts:reports_publish_scheduled', { date: format(values.publishedAt, 'dd.MM.yyyy HH:mm') })
                : t('internal-analysts:reports_publish_now')}
            </Typography>
            {visibility && <Typography variant="body2">{t(visibility.helpKey)}</Typography>}
          </Stack>
        ),
        actionText: t('internal-analysts:reports_publish'),
        data: values,
      })
    },
    () => notifyError(t('internal-analysts:reports_required_missing'))
  )

  const handleConfirm = (confirmed?: ConfirmDialogData<ReportFormValues>) => {
    setConfirmDialogData(undefined)
    if (confirmed?.data) save(confirmed.data)
  }

  const isLoading = !!reportId && reportQuery.isLoading
  const isLoadError = !!reportId && reportQuery.isError

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(reportsListPath)}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {reportId ? t('internal-analysts:reports_edit') : t('internal-analysts:reports_new')}
          </Typography>
        </Stack>
        <Button variant="contained" onClick={handleSave} disabled={isLoading || isLoadError || isSaving || uploads.isUploading}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      {analystsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('internal-analysts:reports_analysts_load_failed')}
        </Alert>
      )}
      {tagsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('internal-analysts:reports_tags_load_failed')}
        </Alert>
      )}

      <QueryStateBoundary isLoading={isLoading} isError={isLoadError} errorText={t('internal-analysts:reports_load_one_failed')} skeletonHeight={480}>
        <Box maxWidth={960}>
          <ReportUploadsContext.Provider value={uploads.track}>
            <ReportForm
              control={control}
              analysts={analystsQuery.data ?? []}
              tags={tagsQuery.data ?? []}
              storedReport={report ? { id: report.id, fileId: report.fileId } : undefined}
              disabled={isSaving}
            />
          </ReportUploadsContext.Provider>
        </Box>
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:reports_discard_title')}
        text={t('internal-analysts:reports_discard_confirm')}
        discardText={t('internal-analysts:reports_discard')}
      />
    </Box>
  )
}
